import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import ArticleIcon from '@mui/icons-material/Article';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

const pages = [
  { title: 'Domů', path: '/', icon: <HomeIcon /> },
  { title: 'Skenovat', path: '/scan', icon: <QrCodeScannerIcon /> },
  { title: 'Průvodce', path: '/guide', icon: <ArticleIcon /> },
  { title: 'Časté dotazy', path: '/faq', icon: <HelpIcon /> },
  { title: 'O aplikaci', path: '/about', icon: <InfoIcon /> },
  { title: 'Kontakt', path: '/contact', icon: <ContactSupportIcon /> },
];

const MainNavigation = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Generate breadcrumbs based on current path
  const generateBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    if (pathnames.length === 0) return null;

    return (
      <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 2, mb: 2 }}>
        <MuiLink
          component={Link}
          to="/"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Domů
        </MuiLink>
        {pathnames.map((name, index) => {
          const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
          const page = pages.find(p => p.path === routeTo);
          const isLast = index === pathnames.length - 1;

          return isLast ? (
            <Typography key={name} color="text.primary">
              {page ? page.title : name}
            </Typography>
          ) : (
            <MuiLink
              key={name}
              component={Link}
              to={routeTo}
              color="inherit"
            >
              {page ? page.title : name}
            </MuiLink>
          );
        })}
      </Breadcrumbs>
    );
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2, fontWeight: 'bold', fontSize: '1.5rem' }}>
        Nechci Lepek
      </Typography>
      <List>
        {pages.map((page) => (
          <ListItem
            key={page.path}
            component={Link}
            to={page.path}
            selected={location.pathname === page.path}
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 107, 107, 0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {page.icon}
            </ListItemIcon>
            <ListItemText primary={page.title} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" sx={{ backgroundColor: 'white', color: '#333' }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo and Mobile Menu */}
            <Typography
              variant="h6"
              noWrap
              component={Link}
              to="/"
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                fontWeight: 700,
                color: '#ff6b6b',
                textDecoration: 'none',
                fontSize: '3rem',
                marginLeft: '12rem',
              }}
            >
              Nechci Lepek
            </Typography>

            {/* Mobile Logo */}
            <Typography
              variant="h6"
              noWrap
              component={Link}
              to="/"
              sx={{
                flexGrow: 1,
                display: { xs: 'flex', md: 'none' },
                fontWeight: 900,
                color: '#ff6b6b',
                textDecoration: 'none',
                justifyContent: 'center',
                fontSize: '2rem',
                marginLeft: '1.5rem',
                
              }}
            >
              Nechci Lepek
            </Typography>

            {/* Mobile Menu Icon */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="menu"
                onClick={handleDrawerToggle}
                sx={{ color: '#ff6b6b' }}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
              {pages.map((page) => (
                <Button
                  key={page.path}
                  component={Link}
                  to={page.path}
                  sx={{
                    my: 2,
                    mx: 1,
                    color: location.pathname === page.path ? '#ff6b6b' : '#666',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      color: '#ff6b6b',
                      backgroundColor: 'rgba(255, 107, 107, 0.1)',
                    },
                  }}
                  startIcon={page.icon}
                >
                  {page.title}
                </Button>
              ))}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Box component="nav">
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Breadcrumbs */}
      <Container maxWidth="xl">
        {generateBreadcrumbs()}
      </Container>
    </>
  );
};

export default MainNavigation;
